import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserData, synchronizeUsers } from '../modules/userdataactions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import Grid from '@mui/material/Grid';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import UserView from './userview';
import UserStatusBoolean from './userstatusboolean';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Spinner from '../common/spinner';
import { useTranslation } from 'react-i18next';

const filterUsers = (users, searchTerm) => {
    if (!users || !searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(
        (user) =>
            (user.name && user.name.toLowerCase().includes(term)) ||
            (user.username && user.username.toLowerCase().includes(term))
    );
};

const UserManagementView = (props) => {
    const orgUsers = useSelector((state) => state.userdata.orgUsers);
    const ccUsers = useSelector((state) => state.userdata.contractCustomerUsers);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [synchronizing, setSynchronizing] = useState(false);

    useEffect(() => {
        if (props.active) {
            dispatch(getUserData());
        }
    }, [props.active, dispatch]);

    const filteredOrgUsers = useMemo(() => filterUsers(orgUsers, searchTerm), [orgUsers, searchTerm]);
    const filteredCcUsers = useMemo(() => filterUsers(ccUsers, searchTerm), [ccUsers, searchTerm]);

    const newUser = () => {
        props.history.push('/admin/user/new');
    };

    const handleSynchronize = async () => {
        setSynchronizing(true);
        try {
            await dispatch(synchronizeUsers());
        } finally {
            setSynchronizing(false);
        }
    };

    return (
        <Grid item xs={12}>
            <br />
            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={t('admin.searchUsers')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
            <br />
            <h4>{t('admin.users')}</h4>
            {orgUsers == null && <Spinner />}
            {filteredOrgUsers != null && filteredOrgUsers.length > 0 && (
                <div>
                    {filteredOrgUsers.map((user) => (
                        <Accordion key={user.id}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Grid container justifyContent="space-between">
                                    <Grid item>
                                        {user.name} -{' '}
                                        <UserStatusBoolean status={{ enabled: user.enabled, amazonStatus: null }} />
                                    </Grid>
                                    <Grid item>{user.username}</Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                                <UserView user={user} history={props.history} />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </div>
            )}
            <br />
            <h4>{t('admin.contractCustomers')}</h4>
            {ccUsers == null && <Spinner />}
            {filteredCcUsers != null && filteredCcUsers.length > 0 && (
                <div>
                    {filteredCcUsers.map((user) => (
                        <Accordion key={user.id}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Grid container justifyContent="space-between">
                                    <Grid item>
                                        {user.name} -{' '}
                                        <UserStatusBoolean status={{ enabled: user.enabled, amazonStatus: null }} />
                                    </Grid>
                                    <Grid item>{user.username}</Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                                <UserView user={user} history={props.history} />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </div>
            )}
            <Footer>
                <Grid item>
                    <Button
                        variant="outlined"
                        color="primary"
                        name="btn-synchronize-users"
                        onClick={handleSynchronize}
                        disabled={synchronizing}
                        startIcon={<SyncIcon />}
                    >
                        {synchronizing ? t('admin.synchronizing') : t('admin.synchronize')}
                    </Button>
                </Grid>
                <Grid item>
                    <Button variant="contained" color="primary" name="btn-new-user" onClick={newUser}>
                        <i className="fas fa-plus"></i>&nbsp;{t('admin.newUser')}
                    </Button>
                </Grid>
            </Footer>
            <FooterPadding />
        </Grid>
    );
};

export default UserManagementView;
